import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Accordion class recipes.
 *
 * Like `Button.recipe` and `TextArea.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-accordion-*` slots that `.ds-accordion` sets in
 * `components/styles/design-system.css`, and that class needs no dark rule at all because every
 * slot it sets names a semantic role and a role already carries a value per theme.
 *
 * ── Where the state comes from, and why it is not `TextArea`'s shape ───────────────────────────
 *
 * The field pair reads `Field.Root`'s `data-focused` / `data-filled` / `data-invalid` through a
 * `group/ti` marker, because there the element that KNOWS the state and the element that draws it
 * are different. Here the trigger is the control, so its own pseudo-classes are the state, and the
 * rules take Button's guard form:
 *
 *   [&:not([data-disabled]):not(:active):hover]   hover, excluded from pressed and disabled
 *   [&:not([data-disabled]):active]               pressed
 *   [&[data-disabled]]                            disabled
 *
 * The guards matter for exactly the reason `Button.recipe` spells out: a MOUSE press matches
 * `:hover` and `:active` at once, the two rules compile to the same specificity, and without
 * `:not(:active)` the winner is decided by whatever order Tailwind happens to emit them in.
 *
 * The one group in this file is `group/accordion-trigger`, and it is not the pattern above. The
 * chevron is a CHILD of the trigger and has no state of its own, so it reads its parent's — which
 * is what a group is for, rather than a way of reaching a distant sibling's state.
 *
 * ── The `data-disabled` the trigger carries ───────────────────────────────────────────────────
 *
 * Base UI 1.7.0 builds the trigger with `focusableWhenDisabled: true`, so a disabled row is NOT
 * given the native `disabled` attribute: it keeps its place in the tab order and is marked with
 * `aria-disabled` instead. That is the accessible behaviour — a control that vanishes from the tab
 * order is a control a keyboard user cannot discover the state of — and it is also why every rule
 * above keys off `data-disabled` rather than `:disabled`, which would never match.
 */

/**
 * The list.
 *
 * The `.ds-accordion` slot class lives HERE rather than on the item, for the reason
 * `textAreaRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders outside the item — a list-level empty state, say — would
 * otherwise fall outside their scope and resolve to inherited colour rather than failing visibly.
 *
 * `group/accordion` carries the icon placement down to every trigger, so the choice is made once
 * on the container and read as a selector rather than threaded through React context. That keeps
 * it in the recipe layer, which is where a visual decision belongs.
 *
 * Items are separated by an 8px gap rather than by collapsing their borders into a shared seam.
 * The seam is the stacked-field contract, and it is wrong here: a field group is one control split
 * into rows, while an accordion list is several independent cards, and the gap is also what gives
 * the focus halo somewhere to be drawn — see `accordionTriggerRecipe`.
 */
export const accordionRootRecipe = cva(['ds-accordion group/accordion', 'flex w-full flex-col gap-[8px]']);

/**
 * One section: the bordered card that holds a row and its panel.
 *
 * NOT `overflow-hidden`, which is the obvious thing to reach for on a rounded box containing an
 * animating child and is wrong twice over. The clipping the height animation needs belongs on the
 * panel, which is the element whose height changes; and clipping here would cut the trigger's
 * focus halo off at the card's edge, leaving an indicator visible on three sides.
 *
 * The radius has no design to match, so it is `TextArea`'s 16px — the two components appear on the
 * same screens and a card with a different corner from the field beside it reads as a mistake.
 */
export const accordionItemRecipe = cva([
  'relative flex w-full flex-col',
  'rounded-[16px]',
  'border-solid border-[length:var(--ds-accordion-border-width)] border-[var(--ds-accordion-border)]',
  'bg-[var(--ds-accordion-bg)]',
  'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

  '[&[data-disabled]]:border-[var(--ds-accordion-border-disabled)]',
  '[&[data-disabled]]:bg-[var(--ds-accordion-bg-disabled)]',
]);

/**
 * The heading that wraps the trigger.
 *
 * Base UI renders an `<h3>`, which is a real heading in the document outline — that is the whole
 * point of the part, since it is what lets a screen reader user jump between sections. It carries
 * no type styling of its own: the visible text is the trigger's, and styling the heading would
 * fight it. `m-0` is load-bearing rather than tidiness, for the reason the field's helper row
 * gives: Preflight is absent from the token layer, so an `<h3>` keeps the browser's default
 * `margin: 1em 0` and the card would open with 21px of empty space above and below its row.
 */
export const accordionHeaderRecipe = cva(['m-0 flex']);

/**
 * The row, which is the control.
 *
 * It fills the card and pays all the padding itself, the same decision the field's control makes
 * and for the same reason: pad the card instead and the button becomes a strip across the middle,
 * so most of what looks like the row is dead to the pointer. Here the whole row is meant to open
 * the section, so every pixel of it has to be the button.
 *
 * ── The resets ────────────────────────────────────────────────────────────────────────────────
 *
 * Preflight is deliberately absent from the token layer, so the browser's own `<button>` styling
 * is still in play: a grey bevelled background, a 2px outset border, centred 13.33px Arial and
 * `text-align: center`. Every one of those is overridden explicitly below. `text-left` is the one
 * that is easy to miss and the most visible when missed — a centred section title does not line up
 * with the panel text underneath it.
 *
 * ── Focus ─────────────────────────────────────────────────────────────────────────────────────
 *
 * The shared two-shadow treatment: a 2px surface-coloured gap over a 4px outer halo.
 *
 * The halo sits outside the card's 1px edge, in the 8px gap between items. That
 * is why the list is spaced rather than seamed and why the card does not clip its overflow: with
 * either of those the other way, the indicator would be drawn underneath the next card.
 *
 * ── Radius ────────────────────────────────────────────────────────────────────────────────────
 *
 * 15px, which is the card's 16px less its 1px border — the inner radius that leaves the hover fill
 * concentric with the edge rather than showing a sliver of card colour at each corner. The bottom
 * pair is squared off while the panel is open, because the row is then the top half of the card
 * and a rounded bottom edge would cut a notch out of the panel above its own content.
 */
export const accordionTriggerRecipe = cva([
  'group/accordion-trigger',
  'm-0 flex w-full appearance-none items-center gap-[12px] border-0 bg-transparent text-left',
  'px-[24px] py-[16px]',
  'rounded-[15px] data-[panel-open]:rounded-b-none',
  'font-sans text-label-lg text-[var(--ds-accordion-title)]',
  'cursor-pointer select-none',
  'transition-[background-color,color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

  // Hover is excluded from pressed and from disabled, so it can never fight either.
  '[&:not([data-disabled]):not(:active):hover]:bg-[var(--ds-accordion-trigger-bg-hover)]',
  '[&:not([data-disabled]):active]:bg-[var(--ds-accordion-trigger-bg-pressed)]',

  '[&[data-disabled]]:cursor-not-allowed',
  '[&[data-disabled]]:text-[var(--ds-accordion-title-disabled)]',

  focusRing(),
]);

/**
 * The label.
 *
 * `min-w-0` is what lets a long title wrap instead of forcing the row wider than the card: a flex
 * item's default `min-width: auto` refuses to shrink below its content, and the chevron would be
 * pushed out through the right edge. The title wraps rather than truncating, because a section
 * title is the only thing naming the content behind it and half of one is not a name.
 */
export const accordionTitleRecipe = cva(['min-w-0 flex-1 text-left']);

/**
 * The chevron.
 *
 * Reads the trigger's state through `group/accordion-trigger` — it is a child with no state of
 * its own, which is what a group is for.
 *
 * The rotation is the second, redundant cue for the open state; the first is `aria-expanded` on
 * the trigger, and the third is the panel being there at all. A caller who removes the icon loses
 * nothing announced.
 */
export const accordionIconRecipe = cva([
  'shrink-0',
  'text-[var(--ds-accordion-icon)]',
  'transition-transform duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'group-data-[panel-open]/accordion-trigger:rotate-180',
  'group-data-[disabled]/accordion-trigger:text-[var(--ds-accordion-icon-disabled)]',

  /**
   * Icon placement, read off the list's `data-icon-placement` rather than passed down as a prop.
   *
   * Reordering the icon rather than reversing the row is what keeps the title on `flex-1` in both
   * arrangements: the label fills whatever is left and the chevron hugs its edge, so a long title
   * wraps the same way whichever side the glyph is on. `flex-row-reverse` would have done the same
   * job and would also have reversed the meaning of every other rule on the row.
   *
   * `order-[-1]` rather than `order-first`, which Tailwind v4 compiles to `order: calc(-infinity)`.
   * One explicit step below the default 0 is all that is needed here, and it is a value a reader
   * can evaluate.
   */
  'group-data-[icon-placement=start]/accordion:order-[-1]',
]);

/**
 * The panel.
 *
 * Height, not max-height, and the value is Base UI's own `--accordion-panel-height` — which it
 * measures and sets as an inline custom property on this element, and leaves as `auto` once the
 * transition has settled. Animating `max-height` to a guessed ceiling is the usual shortcut and it
 * is why so many accordions ease slowly through empty space before the content appears.
 *
 * `data-starting-style` and `data-ending-style` are Base UI's two transition hooks: the first is
 * present for one frame as the panel mounts, the second for the duration of the close. Both pin
 * the height to 0, so the transition runs between 0 and the measured height in each direction. The
 * close hook is also what keeps the content mounted long enough to animate out — Base UI defers
 * the unmount until the animations on this element have finished.
 *
 * `overflow-hidden` belongs here rather than on the card, because this is the element whose height
 * is being clipped.
 *
 * ── Reduced motion ────────────────────────────────────────────────────────────────────────────
 *
 * `motion-reduce:transition-none`, which is not the throwaway it is on the other recipes in this
 * repo. This is the only animated primitive of the five, and an expanding height is a vestibular
 * trigger — WCAG 2.3.3 asks for exactly this. With the transition off the panel snaps to its
 * height instead, and the unmount still happens: Base UI waits for the animations on the element
 * to finish, and an element with no transition has none, so the wait resolves immediately.
 */
export const accordionPanelRecipe = cva([
  'h-[var(--accordion-panel-height)] overflow-hidden',
  /**
   * `--motion-layout`, which is what this declaration should always have read: a panel expanding is
   * an on-screen element MOVING, not a control changing its own tint, and the role table names
   * exactly that case. It had been left on Tailwind's bare ease-out curve and `--motion-control` as
   * the single allowlisted literal in `motionLiterals.test.ts`, because no curve then available
   * equalled that one and the change is visible.
   *
   * It is taken here, deliberately, and it is the one retime in this change:
   *
   *   duration   150ms -> 200ms. A height expansion travels further than any tint in the set and is
   *              the one declaration whose length a reader actually perceives.
   *   curve      Tailwind's ease-out (`cubic-bezier(0, 0, 0.2, 1)`) -> `--ease-movement`, which is
   *              ease-in-out-cubic. A disclosure is a two-way toggle between two on-screen states,
   *              so it accelerates out of rest and brakes into it. A pure deceleration is the right
   *              curve for something ARRIVING from off-screen, which a panel already in the flow is
   *              not.
   *
   * The allowlist is now empty and the guard carries no exception at all.
   *
   * ── The one layout-tier animation in the set, and why it stays ────────────────────────────────
   *
   * `height` enters the renderer at Layout, so it re-measures every frame — the most expensive tier
   * there is, where every other animated declaration in DS 2.0 is composite-only (`opacity`,
   * `scale`) or paint-tier at worst. The usual swap down a tier is `scale-y`, and it is not taken:
   * scaling a panel distorts the type inside it for the whole 200ms, which is worse than the cost
   * it saves. This is a knowing exception, not an oversight.
   *
   * What keeps it affordable is what is being measured. The height comes from Base UI as an inline
   * custom property on THIS element, set once per open, and the transition then runs on `height`
   * directly. That is not the trap it resembles: an animated custom property on a SHARED PARENT
   * forces a style recalculation on every descendant every frame, which is what makes a long
   * drag-driven list degrade with its item count. Here nothing animates the variable, and the
   * element is the only consumer.
   *
   * No `will-change`. Browsers already promote what they can, a layer costs GPU memory, and the
   * rule is to add it only once a shift or a dropped frame has actually been seen. None has.
   */
  'transition-[height] duration-[var(--motion-layout)] ease-[var(--motion-layout-ease)]',
  'motion-reduce:transition-none',
  'data-[starting-style]:h-0 data-[ending-style]:h-0',
]);

/**
 * The panel's content wrapper.
 *
 * A separate element from the panel, and it has to be. The panel's height is animated to a measured
 * value, so padding on it would be added to that height at every frame and the collapsed state
 * would still be `padding-block` tall rather than zero — the content would disappear into a
 * stubborn empty band. Paying the padding one level in keeps the animated box exactly the height of
 * what is inside it.
 *
 * The horizontal inset matches the row's 24px so the body text starts under the title rather than
 * under the chevron.
 */
export const accordionPanelContentRecipe = cva([
  'px-[24px] pt-0 pb-[16px]',
  'font-sans text-body-md text-[var(--ds-accordion-panel-fg)]',
]);
